import { useQuery } from "@apollo/client";
import { ReactElement } from "react";
import * as Yup from "yup";

import { createOrEditResponseScriptInputSchema } from "@stw/yup-validators";

import {
  CreateResponseScriptInput,
  FieldsForResponseScriptGroupFragment,
  ListTagsAndGroupsQuery,
  ListTagsAndGroupsQueryVariables,
} from "~frontend/__generated__/graphql";
import { submitErrorField } from "~frontend/components/forms/FormikErrorBanner";
import { ResponseScriptEditForm } from "~frontend/components/responseScripts/ResponseScriptEditForm";
import { LIST_TAGS_AND_GROUPS } from "~frontend/graphql/tags";
import { useMediaUpload } from "~frontend/hooks/useMediaUpload";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { CampaignForEditor } from "~frontend/routes/subaccounts/campaignEditor/types";
import { useErrorMessageFormatter } from "~frontend/util/errors";

import { ScriptContainer } from "../../routes/subaccounts/campaignEditor/InitialMessage";
import { addClientSideMediaUploadValidation } from "../../routes/subaccounts/campaignEditor/Script/ScriptVariants";
import ErrorCard from "../ErrorCard";
import Loading from "../Loading";
import { FormikDialog } from "../forms/FormikDialog";
import {
  ResponseScriptDialogContent,
  ResponseScriptDialogTitle,
} from "./ScriptEditor";
import formatTagsAndGroups from "./formatTagsAndGroups";

export default function AddScriptDialog({
  open,
  scriptGroups,
  campaign,
  onClose,
  onSubmit,
}: {
  open: boolean;
  scriptGroups: FieldsForResponseScriptGroupFragment[];
  campaign?: CampaignForEditor;
  onClose: () => void;
  onSubmit: (
    values: Omit<CreateResponseScriptInput, "subaccountId">
  ) => Promise<void>;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const getErrorMessage = useErrorMessageFormatter();
  const { uploadMedia } = useMediaUpload();

  const listTagsResult = useQuery<
    ListTagsAndGroupsQuery,
    ListTagsAndGroupsQueryVariables
  >(LIST_TAGS_AND_GROUPS, {
    variables: {
      subaccountId: subaccount.id,
    },
    fetchPolicy: "network-only",
  });

  const groups = scriptGroups.map(({ id, title }) => ({
    id,
    label: title,
  }));

  return (
    <ScriptContainer>
      <FormikDialog
        open={open}
        initialValues={{
          [submitErrorField]: null,
          title: "",
          script: "",
          media: null,
          groupIds: [],
          tagIds: [],
          keywordsToAdd: [],
        }}
        validationSchema={addClientSideMediaUploadValidation(
          createOrEditResponseScriptInputSchema(Yup).fields.input
        )}
        onSubmit={async (values, formik) => {
          const mediaUpload = await uploadMedia(values.media);
          try {
            await onSubmit({
              title: values.title,
              script: values.script,
              mediaUploadId: mediaUpload?.id,
              groupIds: values.groupIds,
              tagIds: values.tagIds,
              keywordsToAdd: values.keywordsToAdd,
            });
            formik.resetForm();
          } catch (e) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(e),
            });
          }
        }}
      >
        <ResponseScriptDialogTitle>
          New Script Response
        </ResponseScriptDialogTitle>
        <ResponseScriptDialogContent>
          {listTagsResult.error && (
            <ErrorCard errors={[listTagsResult.error]} />
          )}
          {listTagsResult.loading && <Loading />}
          {listTagsResult.data && (
            <ResponseScriptEditForm
              onClose={onClose}
              tags={formatTagsAndGroups(listTagsResult.data)}
              groups={groups}
              keywords={[]}
              submitText="Add"
              loadingText="Adding..."
              campaign={campaign}
            />
          )}
        </ResponseScriptDialogContent>
      </FormikDialog>
    </ScriptContainer>
  );
}
